import { Box, TextField, Typography, Chip, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useBookingSectionValidation } from '../../../hooks/useBookingSectionValidation';
import { guestDetailsSchema, type GuestDetailsData } from '@lov/shared';
import { GuestCountSelector } from '../../GuestCountSelector';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useEffect, useCallback } from 'react';
import { useMe } from '../../../../../hooks/auth/useMe';

interface GuestDetailsSectionProps {
  onComplete: (data: unknown, summary: string) => void;
  registerContinue?: (callback: () => void) => void;
  isActive?: boolean;
  /** Previously selected values to restore when section is re-opened */
  initialData?: {
    guestName?: string;
    guestPhone?: string;
    guestCount?: number;
    notes?: string;
  };
}

export function GuestDetailsSection({ onComplete, registerContinue, initialData }: GuestDetailsSectionProps) {
  const { data: user } = useMe();

  const { form, validateAndProceed, shake } = useBookingSectionValidation<GuestDetailsData>(
    guestDetailsSchema,
    {
      guestName: initialData?.guestName ?? (user ? `${user.firstName} ${user.lastName}`.trim() : ''),
      guestPhone: initialData?.guestPhone ?? (user?.phone || ''),
      guestCount: initialData?.guestCount ?? 1,
      notes: initialData?.notes ?? '',
    }
  );

  const {
    register,
    formState: { errors, dirtyFields },
    setValue,
    watch,
  } = form;

  const guestCount = watch('guestCount');

  // Pre-fill from user profile when user data loads
  useEffect(() => {
    if (user) {
      const currentName = form.getValues('guestName');
      const currentPhone = form.getValues('guestPhone');
      const fullName = `${user.firstName} ${user.lastName}`.trim();
      const needsNameFill = !currentName && fullName;
      const needsPhoneFill = !currentPhone && user.phone;
      if (needsNameFill || needsPhoneFill) {
        form.reset({
          guestName: needsNameFill ? fullName : (currentName ?? ''),
          guestPhone: needsPhoneFill ? user.phone : (currentPhone ?? ''),
          guestCount: form.getValues('guestCount') ?? 1,
          notes: form.getValues('notes') ?? '',
        });
      }
    }
  }, [user, form]);

  const handleGuestCountUpdate = (count: number) => {
    setValue('guestCount', count, { shouldValidate: true, shouldDirty: true });
  };

  const handleContinue = useCallback(() => {
    validateAndProceed(() => {
      const data = form.getValues();
      const summary =
        data.guestCount === 1
          ? '1 guest'
          : `${data.guestName || 'Guest'} • ${data.guestCount} guests`;
      onComplete(data, summary);
    });
  }, [validateAndProceed, form, onComplete]);

  useEffect(() => {
    if (registerContinue) {
      registerContinue(handleContinue);
    }
  }, [registerContinue, handleContinue]);

  return (
    <motion.div animate={shake ? { x: [-10, 10, -10, 10, 0] } : {}} transition={{ duration: 0.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
        {guestCount === 1 && user && (user.firstName || user.phone) && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Booking as{' '}
              <strong>{[user.firstName, user.lastName].filter(Boolean).join(' ')}</strong>
              {user.phone && ` (${user.phone})`}
            </Typography>
            <Button variant="text" size="small" onClick={handleContinue}>
              Skip, just me →
            </Button>
          </Box>
        )}

        {guestCount === 1 && !(user && (user.firstName || user.phone)) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip label="Optional" size="small" color="info" variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              For single bookings, you can skip guest details or provide them for faster future
              bookings.
            </Typography>
          </Box>
        )}

        <TextField
          label="Guest Name"
          placeholder="John Doe"
          {...register('guestName')}
          error={!!errors.guestName}
          helperText={
            guestCount === 1 ? 'Optional for single guest' : (errors.guestName?.message as string)
          }
          InputProps={{
            endAdornment:
              !errors.guestName && dirtyFields.guestName ? (
                <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
              ) : undefined,
          }}
        />

        <TextField
          label="Phone Number"
          placeholder="9876543210"
          inputMode="numeric"
          {...register('guestPhone')}
          error={!!errors.guestPhone}
          helperText={
            guestCount === 1
              ? 'Optional for single guest'
              : (errors.guestPhone?.message as string) || 'Required for group bookings'
          }
          InputProps={{
            endAdornment:
              !errors.guestPhone && dirtyFields.guestPhone ? (
                <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
              ) : undefined,
          }}
        />

        {/* Use GuestCountSelector instead of TextField */}
        <GuestCountSelector
          guestCount={guestCount || 1}
          onUpdateCount={handleGuestCountUpdate}
          minGuests={1}
          maxGuests={50}
        />

        <TextField
          label="Special Requests (Optional)"
          placeholder="Any special requirements..."
          multiline
          rows={2}
          {...register('notes')}
          error={!!errors.notes}
          helperText={errors.notes?.message as string}
        />

        {guestCount === 1 && (
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={handleContinue}
            sx={{ mt: 2 }}
            fullWidth
          >
            Continue to Review
          </Button>
        )}
      </Box>
    </motion.div>
  );
}
